import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'lib/api';
import { saveLastPlayedTable } from 'lib/lastTableStorage';
import { navigateInApp } from 'lib/navigation';
import { API_ENDPOINTS } from 'constants/game';
import { BuyInInfo, SessionUser } from 'types/game';
import { buyInBlockMessage } from 'lib/buy-in-messages';

type RefreshSessionFn = () => Promise<SessionUser | null>;
type UserSnapshotFn = () => SessionUser | null;

const BUY_IN_RETRY_DELAYS_MS = [450, 900, 1500];

/** Verify join eligibility, then navigate — server debits full wallet on join. */
export function useTableJoin(refreshSession?: RefreshSessionFn, getUserSnapshot?: UserSnapshotFn) {
  const navigate = useNavigate();
  const [joinLoading, setJoinLoading] = useState(false);
  const [error, setError] = useState('');

  const joinTable = useCallback(async (tableId: number) => {
    setError('');
    setJoinLoading(true);

    const fetchBuyIn = async () => {
      return api.get<BuyInInfo>(API_ENDPOINTS.FRONTEND.LOBBY_BUY_IN(tableId));
    };

    try {
      if (refreshSession) {
        await refreshSession();
      }

      let res = await fetchBuyIn();

      for (const delayMs of BUY_IN_RETRY_DELAYS_MS) {
        if (res.success && res.data?.canJoin) break;
        if (res.success && res.data?.joinBlockReason !== 'balance_unverified') break;

        await new Promise((resolve) => setTimeout(resolve, delayMs));
        if (refreshSession) {
          await refreshSession();
        }
        res = await fetchBuyIn();
      }

      if (!res.success || !res.data) {
        setError(res.message || 'Could not join table — try again');
        return false;
      }

      if (!res.data.canJoin) {
        setError(buyInBlockMessage(res.data));
        return false;
      }

      saveLastPlayedTable(tableId);
      navigateInApp(navigate, `/table/${tableId}`);
      return true;
    } catch {
      setError('Could not join table — try again');
      return false;
    } finally {
      setJoinLoading(false);
    }
  }, [refreshSession, getUserSnapshot, navigate]);

  const navigateToTable = useCallback(
    (tableId: number) => {
      saveLastPlayedTable(tableId);
      navigateInApp(navigate, `/table/${tableId}`);
    },
    [navigate]
  );

  return {
    joinLoading,
    error,
    setError,
    joinTable,
    navigateToTable,
  };
}
